import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { auth, database } from "../../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { ref, push } from "firebase/database";
import emailLogo from "../../assets/email-logo.png";
import "./Auth.css";

const ALERT_THRESHOLD = 3;

const Auth = ({ onSignIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Track failed attempts without re-rendering
  const failedCountRef = useRef(0);

  const sendFailedLoginAlert = async (userEmail, count) => {
    if (count % ALERT_THRESHOLD !== 0) return;

    const alertRef = ref(database, "alerts/failedLogins");
    const alertData = {
      email: userEmail,
      timestamp: new Date().toISOString(),
      message: `User has failed to log in ${count} times.`,
    };

    try {
      await push(alertRef, alertData);
      console.log("Alert sent to Firebase successfully.");
    } catch (error_) {
      console.error("Error sending alert to Firebase:", error_);
    }
  };

  const handleFailedAttempt = async (userEmail) => {
    failedCountRef.current += 1;
    await sendFailedLoginAlert(userEmail, failedCountRef.current);
  };

  const handleSignInWithEmail = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      setError("");
      failedCountRef.current = 0;

      // Check admin status from database
      const sanitizedEmail = userCredential.user.email.replace(/\./g, "-");
      const adminRef = ref(database, `data/settings/admins/${sanitizedEmail}`);

      // Use "get" to fetch the value once
      const snapshot = await import("firebase/database").then(({ get }) => get(adminRef));
      const isAdmin = snapshot.exists() && snapshot.val() === true;

      onSignIn(userCredential.user, isAdmin);
    } catch (error_) {
      console.error("Authentication failed:", error_);
      setError("Invalid credentials. Please try again.");
      await handleFailedAttempt(email);
    }
  };

  return (
    <div className="auth-container">
      <h2>Sign In</h2>

      <div className="auth-field">
        <label htmlFor="auth_email">Email</label>
        <input
          id="auth_email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="auth-field">
        <label htmlFor="auth_password">Password</label>
        <input
          id="auth_password"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <button
        className="email-sign-in"
        onClick={handleSignInWithEmail}
        disabled={!email || !password}
      >
        <img
          src={emailLogo}
          alt="Email Icon"
          className="email-logo"
        />
        <span>Sign in with Email</span>
      </button>

      {error && <div className="auth-error">{error}</div>}
    </div>
  );
};

/* =======================
   PropTypes validation
   ======================= */
Auth.propTypes = {
  onSignIn: PropTypes.func.isRequired,
};

export default Auth;
